import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - Add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('journi_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('journi_token');
      localStorage.removeItem('journi_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to get current user
export const getCurrentUser = () => {
  const userStr = localStorage.getItem('journi_user');
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Creates a new ride posting.
 */
export const createRide = async (rideData) => {
  const user = getCurrentUser();
  if (!user) throw new Error('User not logged in');

  const response = await api.post('/rides/create', {
    driver_id: user.id,
    ...rideData
  });
  return response.data;
};

/**
 * Searches for available rides
 */
export const searchRides = async (searchParams) => {
  const response = await api.get('/rides/search', {
    params: searchParams
  });
  return response.data;
};

/**
 * Sends a request to join a specific ride
 */
export const requestRide = async (rideId) => {
  const response = await api.post('/rides/request', { 
    ride_id: rideId 
  });
  return response.data;
};

/**
 * Fetches the rides associated with the current user
 * Returns: { success, rides: { as_driver: [], as_rider: [] } }
 */
export const getMyRides = async () => {
  try {
    const response = await api.get('/dashboard/rides');
    
    if (!response.data || !response.data.rides) {
      return {
        success: false,
        rides: { as_driver: [], as_rider: [] }
      };
    }
    
    // Backend now properly returns separated arrays
    return {
      success: true,
      rides: {
        as_driver: response.data.rides.as_driver || [],
        as_rider: response.data.rides.as_rider || []
      }
    };
  } catch (error) {
    console.error('Failed to load rides:', error);
    return {
      success: false,
      rides: { as_driver: [], as_rider: [] }
    };
  }
};

/**
 * Get ride requests for driver's rides
 */
export const getDriverRequests = async () => {
  try {
    const response = await api.get('/dashboard/requests');
    return {
      success: true,
      requests: response.data.requests || []
    };
  } catch (error) {
    console.error('Failed to load driver requests:', error);
    return {
      success: false,
      requests: []
    };
  }
};


/**
 * Driver responds to ride request (accept/decline)
 */
export const respondToRideRequest = async (rideId, riderId, action, reason = null) => {
  const response = await api.post('/rides/respond', {
    ride_id: rideId,
    rider_id: riderId,
    action, // 'accept' or 'decline'
    reason
  });
  return response.data;
};

/**
 * Cancel a ride (works for both driver and rider)
 */
export const cancelRide = async (rideId, role, reason = null) => {
  const response = await api.post('/rides/cancel', {
    ride_id: rideId,
    role, // 'driver' or 'rider'
    reason
  });
  return response.data;
};

/**
 * USER LOCATIONS API
 */

// Get user's saved locations
export const getUserLocations = async () => {
  const response = await api.get('/user/locations');
  return response.data;
};

// Add new location
export const addUserLocation = async (locationData) => {
  const response = await api.post('/user/locations', locationData);
  return response.data;
};

// Update location
export const updateUserLocation = async (locationId, updates) => {
  const response = await api.put('/user/locations', {
    id: locationId,
    ...updates
  });
  return response.data;
};

// Delete location
export const deleteUserLocation = async (locationId) => {
  const response = await api.delete('/user/locations', {
    data: { id: locationId }
  });
  return response.data;
};

// Get recommended rides near user's locations
export const getRecommendedRides = async () => {
  const response = await api.get('/user/rides/recommended');
  return response.data;
};

export default api;